// =====================================================
// PRICING RULE FORM DIALOG
// Stage 2: Participants & Pricing
// Dialog for creating/editing pricing rules
// =====================================================

import { useEffect } from 'react';
import { AlertTriangle, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { usePricingRuleMutations } from '../hooks/usePricingRules';
import { usePricingRuleForm } from '../hooks/usePricingRuleForm';
import { usePricingLookups } from '../hooks/usePricingLookups';
import { ConditionBuilder } from './ConditionBuilder';
import { RULE_TEMPLATES } from '../utils/conditionDrafts';
import { calculateDiscountPreview, getActionValuePlaceholder } from '@/models/pricingRule';
import { formatPrice } from '@/models/event';
import type { PricingRule, PricingActionType } from '@/types/pricing.types';

interface PricingRuleFormDialogProps {
  eventId: string;
  /** Event base price: used for the live example and to reject higher fixed prices */
  basePrice?: number;
  rule?: PricingRule | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ACTION_OPTIONS: { value: PricingActionType; label: string }[] = [
  { value: 'PERCENTAGE_DISCOUNT', label: 'Descuento %' },
  { value: 'FIXED_DISCOUNT', label: 'Descuento Bs' },
  { value: 'SET_PRICE', label: 'Precio fijo' },
];

const VALUE_LABELS: Record<PricingActionType, string> = {
  PERCENTAGE_DISCOUNT: 'Porcentaje de descuento',
  FIXED_DISCOUNT: 'Monto a descontar (Bs)',
  SET_PRICE: 'Precio que pagan (Bs)',
};

const formatBs = formatPrice;

export function PricingRuleFormDialog({
  eventId,
  basePrice,
  rule,
  open,
  onOpenChange,
}: PricingRuleFormDialogProps) {
  const mutations = usePricingRuleMutations(eventId);
  const form = usePricingRuleForm(rule);
  const { degrees, districts, regions, lookups, isLoading: isLoadingData } = usePricingLookups();

  const isEditMode = !!rule;
  const isSubmitting = mutations.isCreating || mutations.isUpdating;
  const { formData, errors, resetForm } = form;

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, rule, resetForm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.validate(basePrice)) {
      return;
    }

    try {
      if (isEditMode && rule) {
        await mutations.updatePricingRule(rule.id, form.getUpdateData(lookups));
      } else {
        await mutations.createPricingRule(form.getCreateData(eventId, lookups));
      }
      onOpenChange(false);
    } catch (error) {
      // Error handled by mutation
      console.error('Form submission error:', error);
    }
  };

  const hasBase = basePrice !== undefined && basePrice > 0;
  const preview =
    hasBase && formData.action_type && formData.action_value !== null
      ? calculateDiscountPreview(basePrice, formData.action_type, formData.action_value)
      : null;
  const exceedsBase =
    hasBase && formData.action_type === 'FIXED_DISCOUNT' && (formData.action_value ?? 0) > basePrice;

  const showTemplates = !isEditMode && formData.conditions.length === 0 && formData.action_type === null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Editar regla de precios' : 'Nueva regla de precios'}</DialogTitle>
          <DialogDescription>
            Una regla dice <strong>a quiénes</strong> aplica y <strong>qué precio</strong> pagan. Cuando varias reglas
            coinciden con una persona, gana la que está más arriba en la lista.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {showTemplates && (
            <div className="space-y-2">
              <p className="flex items-center gap-1.5 text-sm font-medium">
                <Sparkles className="size-4" /> Empezar desde una plantilla
              </p>
              <div className="flex flex-wrap gap-2">
                {RULE_TEMPLATES.map((t) => (
                  <Button
                    key={t.id}
                    type="button"
                    variant="outline"
                    size="sm"
                    title={t.description}
                    onClick={() => form.applyTemplate(t)}
                  >
                    {t.label}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">O arma la regla desde cero abajo.</p>
            </div>
          )}

          {/* 1. Who */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">1. ¿A quiénes aplica?</CardTitle>
              <CardDescription>Define los criterios que debe cumplir el participante.</CardDescription>
            </CardHeader>
            <CardContent>
              <ConditionBuilder
                drafts={formData.conditions}
                degrees={degrees}
                districts={districts}
                regions={regions}
                isLoadingData={isLoadingData}
                showProblems={form.showProblems}
                onAdd={() => form.addCondition()}
                onUpdate={form.updateCondition}
                onRemove={form.removeCondition}
              />
              {errors.conditions && <p className="mt-2 text-sm text-destructive">{errors.conditions}</p>}
            </CardContent>
          </Card>

          {/* 2. How much */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">2. ¿Qué precio pagan?</CardTitle>
              <CardDescription>
                {hasBase ? `Precio base del evento: ${formatBs(basePrice)}.` : 'Elige cómo se calcula el precio.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <SegmentedControl
                options={ACTION_OPTIONS}
                value={(formData.action_type ?? '') as PricingActionType}
                onChange={(value) => {
                  if (value !== formData.action_type) {
                    form.updateField('action_type', value);
                    form.updateField('action_value', null);
                  }
                }}
                className="w-full"
              />
              {errors.actionType && <p className="text-sm text-destructive">{errors.actionType}</p>}

              {formData.action_type && (
                <div className="space-y-2">
                  <Label htmlFor="action_value">{VALUE_LABELS[formData.action_type]}</Label>
                  <Input
                    id="action_value"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.action_value ?? ''}
                    onChange={(e) =>
                      form.updateField('action_value', e.target.value === '' ? null : parseFloat(e.target.value))
                    }
                    placeholder={getActionValuePlaceholder(formData.action_type)}
                    className={errors.actionValue ? 'border-destructive' : ''}
                  />
                  {errors.actionValue && <p className="text-sm text-destructive">{errors.actionValue}</p>}
                </div>
              )}

              {preview && (
                <div className="rounded-lg border bg-muted/50 p-4 text-sm">
                  <p className="mb-2 font-medium">Ejemplo con el precio base ({formatBs(basePrice!)})</p>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Ahorro:</span>
                    <span className="font-medium text-green-600">
                      {formatBs(preview.discountAmount)} ({preview.discountPercent.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="mt-2 flex justify-between border-t pt-2">
                    <span className="font-medium">Pagan:</span>
                    <span className="text-lg font-bold">{formatBs(preview.finalPrice)}</span>
                  </div>
                  {exceedsBase && (
                    <p className="mt-2 flex items-center gap-1 text-xs text-amber-600">
                      <AlertTriangle className="size-3" />
                      El descuento supera el precio base: el precio final será Bs 0.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* 3. Details */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre de la regla</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => form.updateField('name', e.target.value)}
                placeholder={form.getSuggestedName(lookups)}
                className={errors.name ? 'border-destructive' : ''}
              />
              <p className="text-xs text-muted-foreground">
                Si lo dejas vacío se usa el nombre sugerido (en gris).
              </p>
              {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descripción (opcional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => form.updateField('description', e.target.value)}
                placeholder="Nota interna para recordar el motivo de la regla…"
                rows={2}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label>Regla activa</Label>
                <p className="text-sm text-muted-foreground">Si la desactivas, deja de aplicarse sin borrarla.</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => form.updateField('is_active', checked)}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting || (isEditMode && !form.hasChanges)}>
              {isSubmitting ? 'Guardando...' : isEditMode ? 'Guardar cambios' : 'Crear regla'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
